import { supabase } from "../supabase/client";
import { demoService } from "../demo/demoService";
import { isAbortError } from "../utils/abort";

// Base API URL
const API_URL = process.env.REACT_APP_API_URL || "";

// Helper to get auth headers
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  if (demoService.isDemo()) {
    return {
      "Content-Type": "application/json",
    };
  }
  let session = null;
  try {
    const {
      data: { session: activeSession },
    } = await supabase.auth.getSession();
    session = activeSession;
  } catch (error) {
    if (isAbortError(error)) {
      console.debug("[DEBUG] stripeConnect getSession aborted", error);
    } else {
      console.error("[DEBUG] stripeConnect getSession failed", error);
    }
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (session?.access_token) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
  }

  return headers;
};

export interface StripeAccount {
  id: string;
  stripe_user_id: string;
  business_name?: string;
  email?: string;
  livemode: boolean;
  connected_at: string;
  status: string;
  balance?: {
    available: number;
    pending: number;
    currency: string;
  };
  error?: string;
}

export interface StripeTransaction {
  id: string;
  account_id: string;
  amount: number;
  currency: string;
  status: string;
  description?: string;
  customer_email?: string;
  customer_name?: string;
  payment_method_type?: string;
  receipt_url?: string;
  refunded: boolean;
  refunded_amount: number;
  date: string;
  created_at: string;
  source: "stripe";
}

export const stripeConnectService = {
  // Get OAuth URL to initiate Stripe Connect
  getConnectUrl: async (): Promise<string> => {
    if (demoService.isDemo()) {
      return "";
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/stripe/connect`, {
      headers,
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to get Stripe connect URL");
    }

    const data = await response.json();
    return data.url;
  },

  // Get connected Stripe accounts
  getAccounts: async (): Promise<StripeAccount[]> => {
    if (demoService.isDemo()) {
      return [];
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/stripe/accounts`, {
      headers,
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to fetch Stripe accounts");
    }

    const data = await response.json();
    return data.accounts;
  },

  // Get Stripe transactions for date range
  getTransactions: async (
    startDate: string,
    endDate: string
  ): Promise<StripeTransaction[]> => {
    if (demoService.isDemo()) {
      return [];
    }
    const params = new URLSearchParams();
    params.append("start_date", startDate);
    params.append("end_date", endDate);

    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/stripe/transactions?${params.toString()}`,
      { headers }
    );

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to fetch Stripe transactions");
    }

    const data = await response.json();
    return data.transactions;
  },

  // Disconnect a Stripe account
  disconnect: async (connectionId: string): Promise<void> => {
    if (demoService.isDemo()) {
      return;
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/stripe/disconnect`, {
      method: "POST",
      headers,
      body: JSON.stringify({ connection_id: connectionId }),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to disconnect Stripe account");
    }
  },

  // Initiate Stripe Connect - redirects user to Stripe
  initiateConnect: async (): Promise<void> => {
    if (demoService.isDemo()) {
      return;
    }
    const url = await stripeConnectService.getConnectUrl();
    window.location.href = url;
  },
};
